<template>
  <div class="data-statistics">
    <section class="cockpit-hero">
      <div>
        <div class="cockpit-eyebrow">运营驾驶舱</div>
        <h2 class="cockpit-title">数据统计</h2>
        <p class="cockpit-desc">聚合报名项目、会员等级与 ROI 城市消费排行，快速判断今日投放与转化表现。</p>
      </div>
      <div class="cockpit-date">
        <span>今日</span>
        <strong>{{ todayText }}</strong>
      </div>
    </section>

    <section class="cockpit-metrics">
      <article v-for="item in cockpitCards" :key="item.key" class="metric-card" :class="`metric-card--${item.tone}`">
        <div class="metric-card__label">{{ item.label }}</div>
        <div class="metric-card__value">{{ item.value }}</div>
        <div class="metric-card__note">{{ item.note }}</div>
      </article>
    </section>

    <el-row :gutter="18" class="cockpit-grid">
      <el-col :xs="24" :sm="24" :md="14" :lg="14" :xl="14">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="card-header">
            <div class="header-left">
              <el-button 
                v-if="currentLevel === 2" 
                type="text" 
                icon="el-icon-back" 
                @click="goBack"
              >返回</el-button>
              <span class="title">{{ chartTitle }}</span>
            </div>
            <span v-if="currentLevel === 2" class="breadcrumb">
              全部分类 / {{ currentCategory.categoryName }}
            </span>
          </div>
          <div ref="projectChart" class="chart-container" v-loading="projectLoading"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="24" :md="10" :lg="10" :xl="10">
        <el-card shadow="never" class="chart-card">
          <div slot="header" class="card-header">
            <span class="title">会员等级统计</span>
          </div>
          <div ref="memberChart" class="chart-container" v-loading="memberLoading"></div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="18" class="cockpit-grid cockpit-grid--bottom">
      <el-col :xs="24" :sm="24" :md="14" :lg="14" :xl="14">
        <el-card shadow="never" class="chart-card city-rank-card">
          <div slot="header" class="card-header">
            <div class="header-left">
              <span class="title">今日城市消费 Top 5</span>
              <el-tag size="mini" type="warning" effect="plain">ROI监控</el-tag>
            </div>
            <span class="breadcrumb">按当天消费总额降序</span>
          </div>
          <div ref="cityChart" class="chart-container city-chart-container" v-loading="cityTopLoading"></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="24" :md="10" :lg="10" :xl="10">
        <el-card shadow="never" class="chart-card city-list-card">
          <div slot="header" class="card-header">
            <span class="title">城市排行明细</span>
          </div>
          <div class="city-rank-list" v-loading="cityTopLoading">
            <div v-if="cityTopData.length">
              <div v-for="(item, index) in cityTopData" :key="`${item.cityName}-${index}`" class="city-rank-row">
                <span class="city-rank-index">{{ index + 1 }}</span>
                <span class="city-rank-name">{{ item.cityName }}</span>
                <span class="city-rank-bar"><i :style="{ width: item.percent + '%' }"></i></span>
                <strong class="city-rank-value">{{ formatMoney(item.consumption) }}</strong>
              </div>
            </div>
            <el-empty v-else description="暂无今日城市消费数据" :image-size="88" />
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import api from '../services/api';
import * as echarts from 'echarts';

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default {
  name: 'DataStatistics',
  data() {
    return {
      projectLoading: false,
      memberLoading: false,
      cityTopLoading: false,
      projectChart: null,
      memberChart: null,
      cityChart: null,
      chartData: [],
      memberData: [],
      cityTopData: [],
      currentLevel: 1,
      currentCategory: null,
      categoryColors: {
        'POSTGRADUATE': ['#667eea', '#764ba2'],
        'CIVIL_SERVANT': ['#f093fb', '#f5576c'],
        'HIGH_SCHOOL': ['#4facfe', '#00f2fe']
      }
    };
  },
  computed: {
    chartTitle() {
      return this.currentLevel === 1 ? '报名项目统计（点击柱子查看详情）' : '二级项目统计';
    },
    todayText() {
      return formatDate(new Date());
    },
    projectTotal() {
      return this.chartData.reduce((sum, item) => {
        const children = Array.isArray(item.children) ? item.children : [];
        return sum + children.reduce((childSum, child) => childSum + (Number(child.count) || 0), 0);
      }, 0);
    },
    memberTotal() {
      return this.memberData.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
    },
    cityTopTotal() {
      return this.cityTopData.reduce((sum, item) => sum + (Number(item.consumption) || 0), 0);
    },
    topCityName() {
      return this.cityTopData[0] ? this.cityTopData[0].cityName : '-';
    },
    cockpitCards() {
      return [
        {
          key: 'project',
          label: '报名项目累计',
          value: `${this.projectTotal} 人次`,
          note: '覆盖当前全部项目分类',
          tone: 'blue'
        },
        {
          key: 'member',
          label: '会员样本量',
          value: `${this.memberTotal} 人`,
          note: '按会员等级结构统计',
          tone: 'green'
        },
        {
          key: 'city',
          label: '今日城市消费 Top 5',
          value: this.formatMoney(this.cityTopTotal),
          note: `最高城市：${this.topCityName}`,
          tone: 'orange'
        }
      ];
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
      this.loadProjectData();
      this.loadMemberData();
      this.loadCityTopData();
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.projectChart) {
      this.projectChart.dispose();
    }
    if (this.memberChart) {
      this.memberChart.dispose();
    }
    if (this.cityChart) {
      this.cityChart.dispose();
    }
  },
  methods: {
    getEcharts() {
      return echarts;
    },
    initCharts() {
      const echarts = this.getEcharts();
      if (echarts) {
        if (this.$refs.projectChart) {
          this.projectChart = echarts.init(this.$refs.projectChart);
          this.bindProjectChartEvents();
        }
        if (this.$refs.memberChart) {
          this.memberChart = echarts.init(this.$refs.memberChart);
        }
        if (this.$refs.cityChart) {
          this.cityChart = echarts.init(this.$refs.cityChart);
        }
      }
    },
    bindProjectChartEvents() {
      this.projectChart.on('click', (params) => {
        if (this.currentLevel === 1 && params.dataIndex !== undefined) {
          this.drillDown(params.dataIndex);
        }
      });
    },
    handleResize() {
      if (this.projectChart) {
        this.projectChart.resize();
      }
      if (this.memberChart) {
        this.memberChart.resize();
      }
      if (this.cityChart) {
        this.cityChart.resize();
      }
    },
    async loadProjectData() {
      this.projectLoading = true;
      try {
        const res = await api.post('/statistics/project-enrollment', {});
        if (res.data.success) {
          this.chartData = res.data.data;
          this.renderLevel1Chart();
        } else {
          this.$message.error(res.data.message || '获取统计数据失败');
        }
      } catch (e) {
        this.$message.error('获取统计数据失败');
      }
      this.projectLoading = false;
    },
    async loadMemberData() {
      this.memberLoading = true;
      try {
        const res = await api.post('/statistics/member-level', {});
        if (res.data.success) {
          this.memberData = res.data.data;
          this.renderMemberChart();
        } else {
          this.$message.error(res.data.message || '获取会员等级统计失败');
        }
      } catch (e) {
        this.$message.error('获取会员等级统计失败');
      }
      this.memberLoading = false;
    },
    async loadCityTopData() {
      this.cityTopLoading = true;
      try {
        const today = this.todayText;
        const res = await api.post('/roi/audit/list', {
          statType: 'day',
          dateRange: [today, today],
          page: 1,
          pageSize: 1000
        });
        if (res.data.success) {
          const rows = ((res.data.data && res.data.data.list) || [])
            .map((item) => ({
              cityName: item.cityName || '未标记城市',
              consumption: Number(item.consumption) || 0
            }))
            .filter((item) => item.consumption > 0)
            .sort((a, b) => b.consumption - a.consumption)
            .slice(0, 5);
          const max = rows[0] ? rows[0].consumption : 0;
          this.cityTopData = rows.map((item) => ({
            ...item,
            percent: max > 0 ? Math.max(8, Math.round((item.consumption / max) * 100)) : 0
          }));
          this.renderCityConsumptionChart();
        } else {
          this.$message.error(res.data.message || '获取城市消费排行失败');
        }
      } catch (e) {
        this.$message.error('获取城市消费排行失败');
      }
      this.cityTopLoading = false;
    },
    formatMoney(value) {
      const n = Number(value) || 0;
      return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    },
    getGradientColor(categoryCode) {
      const echarts = this.getEcharts();
      const colors = this.categoryColors[categoryCode] || ['#409EFF', '#66b1ff'];
      return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: colors[0] },
        { offset: 1, color: colors[1] }
      ]);
    },
    renderMemberChart() {
      const echarts = this.getEcharts();
      if (!echarts || !this.memberChart) return;

      const colorList = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];
      
      const seriesData = this.memberData.map((item, index) => ({
        value: item.count,
        name: item.levelName,
        itemStyle: {
          color: colorList[index % colorList.length]
        }
      }));

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} 人 ({d}%)'
        },
        legend: {
          orient: 'horizontal',
          bottom: '5%',
          left: 'center',
          textStyle: {
            color: '#606266'
          }
        },
        // graphic: {
        //   type: 'text',
        //   left: 'center',
        //   top: '38%',
        //   style: {
        //     text: '总计',
        //     fontSize: 14,
        //     fill: '#909399',
        //     textAlign: 'center'
        //   }
        // },
        series: [{
          name: '会员等级',
          type: 'pie',
          radius: ['50%', '70%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 24,
              fontWeight: 'bold',
              formatter: '{b}\n{c} 人'
            }
          },
          labelLine: {
            show: false
          },
          data: seriesData
        }]
      };

      this.memberChart.setOption(option, true);
    },
    renderCityConsumptionChart() {
      const echarts = this.getEcharts();
      if (!echarts || !this.cityChart) return;
      const names = this.cityTopData.map((item) => item.cityName).reverse();
      const values = this.cityTopData.map((item) => item.consumption).reverse();
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const item = params[0];
            return `${item.name}<br/>今日消费：${this.formatMoney(item.value)}`;
          }
        },
        grid: {
          left: '4%',
          right: '8%',
          top: '10%',
          bottom: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            color: '#7b8794',
            formatter: (value) => this.formatMoney(value)
          },
          splitLine: { lineStyle: { color: '#edf1f7', type: 'dashed' } }
        },
        yAxis: {
          type: 'category',
          data: names,
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: { color: '#303133', fontWeight: 600 }
        },
        series: [{
          name: '今日消费',
          type: 'bar',
          barWidth: 18,
          data: values,
          itemStyle: {
            borderRadius: [0, 9, 9, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#f6ad55' },
              { offset: 1, color: '#ed8936' }
            ])
          },
          label: {
            show: true,
            position: 'right',
            color: '#475569',
            formatter: ({ value }) => this.formatMoney(value)
          }
        }]
      };
      this.cityChart.setOption(option, true);
    },
    renderLevel1Chart() {
      this.currentLevel = 1;
      this.currentCategory = null;
      
      const xAxisData = this.chartData.map(item => item.categoryName);
      const seriesData = this.chartData.map(item => {
        const total = item.children.reduce((sum, child) => sum + child.count, 0);
        return {
          value: total,
          itemStyle: {
            color: this.getGradientColor(item.categoryCode)
          }
        };
      });

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const item = params[0];
            return `${item.name}<br/>报名人数: ${item.value} 人次<br/><span style="color:#909399;font-size:12px">点击查看详情</span>`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            fontSize: 14,
            color: '#303133',
            fontWeight: 'bold'
          },
          axisLine: { lineStyle: { color: '#E4E7ED' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          name: '人次',
          nameTextStyle: { color: '#909399', fontSize: 12 },
          axisLabel: { color: '#606266' },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } }
        },
        series: [{
          name: '报名人数',
          type: 'bar',
          barWidth: '40%',
          data: seriesData,
          itemStyle: { borderRadius: [8, 8, 0, 0], cursor: 'pointer' },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#303133'
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }]
      };

      this.projectChart.setOption(option, true);
    },
    drillDown(index) {
      const category = this.chartData[index];
      if (!category || !category.children || category.children.length === 0) {
        this.$message.info('该分类暂无子项目数据');
        return;
      }
      
      this.currentLevel = 2;
      this.currentCategory = category;
      
      const xAxisData = category.children.map(item => item.projectName);
      const seriesData = category.children.map(item => ({
        value: item.count,
        itemStyle: {
          color: this.getGradientColor(category.categoryCode)
        }
      }));

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: '{b}<br/>报名人数: {c} 人次'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            interval: 0,
            rotate: xAxisData.length > 5 ? 30 : 0,
            fontSize: 12,
            color: '#606266'
          },
          axisLine: { lineStyle: { color: '#E4E7ED' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          name: '人次',
          nameTextStyle: { color: '#909399', fontSize: 12 },
          axisLabel: { color: '#606266' },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } }
        },
        series: [{
          name: '报名人数',
          type: 'bar',
          barWidth: '50%',
          data: seriesData,
          itemStyle: { borderRadius: [6, 6, 0, 0] },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#606266'
          }
        }]
      };

      this.projectChart.setOption(option, true);
    },
    goBack() {
      this.renderLevel1Chart();
    }
  }
};
</script>

<style scoped>
.data-statistics {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  padding: 10px;
  overflow: auto;
}

.cockpit-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
  border: 1px solid #dfe7f3;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(248, 250, 252, 0.96), rgba(239, 246, 255, 0.96)),
    #f8fafc;
}

.cockpit-eyebrow {
  color: #3b82f6;
  font-size: 12px;
  font-weight: 700;
}

.cockpit-title {
  margin: 6px 0 8px;
  color: #1f2937;
  font-size: 26px;
  line-height: 1.2;
}

.cockpit-desc {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

.cockpit-date {
  min-width: 132px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #dbeafe;
  background: #ffffff;
  text-align: right;
}

.cockpit-date span {
  display: block;
  color: #94a3b8;
  font-size: 12px;
}

.cockpit-date strong {
  display: block;
  margin-top: 4px;
  color: #1d4ed8;
  font-size: 18px;
}

.cockpit-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.metric-card {
  position: relative;
  overflow: hidden;
  min-height: 118px;
  padding: 18px;
  border-radius: 8px;
  border: 1px solid #e5eaf3;
  background: #ffffff;
}

.metric-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 4px;
  background: currentColor;
  opacity: 0.8;
}

.metric-card__label {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.metric-card__value {
  margin-top: 12px;
  color: #111827;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 700;
}

.metric-card__note {
  margin-top: 10px;
  color: #94a3b8;
  font-size: 12px;
}

.metric-card--blue {
  color: #3b82f6;
}

.metric-card--green {
  color: #16a34a;
}

.metric-card--orange {
  color: #ea580c;
}

.cockpit-grid {
  display: flex;
}

.cockpit-grid--bottom {
  min-height: 340px;
}

.data-statistics .el-col {
  display: flex;
  margin-bottom: 0;
}

.chart-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.data-statistics >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left .title {
  font-weight: bold;
}

.breadcrumb {
  color: #909399;
  font-size: 13px;
}

.chart-container {
  flex: 1;
  min-height: 400px;
}

.city-chart-container {
  min-height: 300px;
}

.city-rank-list {
  flex: 1;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.city-rank-row {
  display: grid;
  grid-template-columns: 30px minmax(72px, 110px) minmax(0, 1fr) minmax(90px, auto);
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #edf2f7;
}

.city-rank-row:last-child {
  border-bottom: none;
}

.city-rank-index {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ea580c;
  background: #fff7ed;
  font-weight: 700;
  font-size: 12px;
}

.city-rank-name {
  color: #334155;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.city-rank-bar {
  height: 8px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
}

.city-rank-bar i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #f6ad55, #ed8936);
}

.city-rank-value {
  color: #111827;
  text-align: right;
}

@media (max-width: 992px) {
  .cockpit-hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .cockpit-date {
    text-align: left;
  }

  .cockpit-metrics {
    grid-template-columns: 1fr;
  }

  .data-statistics .el-col + .el-col {
    margin-top: 18px;
  }
}
</style>
